import { PermissionsCenterFeature } from '@cast/types-permissions-center';

import { FEATURES_ORDER, FEATURES_URL_PARAM_NAME } from './_constants';
import type { GroupedFeatures, UnresolvedFeatureType } from './_types';
import type { AllFeatureTypes } from './types';

export const parseFeatureSelection = (
  searchParams: URLSearchParams
): UnresolvedFeatureType[] => {
  const value = searchParams.get(FEATURES_URL_PARAM_NAME);
  if (!value) {
    return [];
  }
  return value.split(',').filter(Boolean);
};

export const serializeFeatureSelection = (
  featureTypes: UnresolvedFeatureType[]
): string => featureTypes.join(',');

const getFeatureOrderIndex = (
  clusterFeature: PermissionsCenterFeature
): number => {
  const index = FEATURES_ORDER.indexOf(clusterFeature.type);
  return index === -1 ? Number.POSITIVE_INFINITY : index;
};

export const sortFeatures = (
  features: PermissionsCenterFeature[]
): PermissionsCenterFeature[] =>
  [...features].sort(
    (featureA, featureB) =>
      getFeatureOrderIndex(featureA) - getFeatureOrderIndex(featureB)
  );

export const groupFeaturesByScope = (
  features: PermissionsCenterFeature[]
): GroupedFeatures =>
  features.reduce<GroupedFeatures>(
    (groups, feature) => {
      switch (feature.category) {
        case 'ORGANIZATION':
          groups.organization.push(feature);
          break;
        case 'CLUSTER':
          groups.cluster.push(feature);
          break;
        default:
          groups.other.push(feature);
          break;
      }
      return groups;
    },
    { organization: [], cluster: [], other: [] }
  );

/** A parent feature is required by selection when it is directly selected or any of its sub-features is. */
export const isFeatureRequiredBySelection = ({
  feature,
  selectedFeatureTypes,
}: {
  feature: PermissionsCenterFeature;
  selectedFeatureTypes: AllFeatureTypes[];
}): boolean => {
  return (
    selectedFeatureTypes.includes(feature.type) ||
    feature.subFeatures.some((subFeature) =>
      selectedFeatureTypes.includes(subFeature.type)
    )
  );
};

/**
 * Filters unresolved feature type strings against the catalog, dropping any
 * that don't match a known feature or sub-feature type.
 */
export const resolveFeatureTypes = ({
  features,
  unresolvedFeatureTypes,
}: {
  features: PermissionsCenterFeature[];
  unresolvedFeatureTypes: UnresolvedFeatureType[];
}): AllFeatureTypes[] => {
  const knownTypes = new Set<string>(
    features.flatMap((feature) => [
      feature.type,
      ...feature.subFeatures.map((subFeature) => subFeature.type),
    ])
  );

  return unresolvedFeatureTypes.filter(
    (featureType): featureType is AllFeatureTypes => knownTypes.has(featureType)
  );
};

/** Ensures the selection is structurally complete: adds parent features implicitly required by selected sub-features. */
export const resolveFeatureSelection = ({
  features,
  selectedFeatureTypes,
}: {
  features: PermissionsCenterFeature[];
  selectedFeatureTypes: AllFeatureTypes[];
}): AllFeatureTypes[] => {
  const additions = features
    .filter((feature) =>
      isFeatureRequiredBySelection({
        feature,
        selectedFeatureTypes,
      })
    )
    .map((feature) => feature.type);

  return Array.from(new Set([...selectedFeatureTypes, ...additions]));
};
